import React, { useState, useMemo } from 'react';
import { Search, Download, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Client, ClientType } from '../../../types/common';

interface ClientTableProps {
  clients: Client[];
  clientType: ClientType;
  nMonths: number;
  onClientClick?: (c: Client) => void;
  loading?: boolean;
}

type SortKey = 'name' | 'firstInvoice' | 'revenue' | 'avgPerMonth' | 'lastInvoice';

const fmtUSD = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M`
    : n >= 1_000 ? `$${(n / 1_000).toFixed(1)}K`
    : `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

export const ClientTable: React.FC<ClientTableProps> = ({
  clients, clientType, nMonths, onClientClick, loading = false,
}) => {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('revenue');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const showFirstInvoice = clientType !== 'recurring';

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const SortIcon = ({ k }: { k: SortKey }) => {
    if (sortKey !== k) return <ArrowUpDown size={13} className="text-slate-300 ml-1" />;
    return sortDir === 'asc'
      ? <ArrowUp size={13} className="text-blue-500 ml-1" />
      : <ArrowDown size={13} className="text-blue-500 ml-1" />;
  };

  const Th = ({ k, label, align = 'left', className = '' }: { k: SortKey; label: string; align?: string; className?: string }) => (
    <th
      onClick={() => handleSort(k)}
      className={`px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500 cursor-pointer hover:text-slate-800 select-none whitespace-nowrap text-${align} ${className}`}
    >
      <span className="inline-flex items-center gap-0.5">
        {label}<SortIcon k={k} />
      </span>
    </th>
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return clients.filter(c =>
      !q || c.name.toLowerCase().includes(q)
    );
  }, [clients, search]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let aV: string | number;
      let bV: string | number;
      switch (sortKey) {
        case 'name':         aV = a.name.toLowerCase(); bV = b.name.toLowerCase(); break;
        case 'firstInvoice': aV = a.firstInvoice ?? ''; bV = b.firstInvoice ?? ''; break;
        case 'revenue':      aV = a.revenue;            bV = b.revenue;            break;
        case 'avgPerMonth':  aV = a.revenue / nMonths;  bV = b.revenue / nMonths;  break;
        case 'lastInvoice':  aV = a.lastInvoice ?? '';  bV = b.lastInvoice ?? '';  break;
        default:             aV = a.revenue;            bV = b.revenue;
      }
      if (aV < bV) return sortDir === 'asc' ? -1 : 1;
      if (aV > bV) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, sortKey, sortDir, nMonths]);

  const handleExport = () => {
    const cols = ['Client', showFirstInvoice ? 'First Invoice' : null, 'Period Revenue', 'Avg/Month', 'Last Invoice'].filter(Boolean);
    const rows = sorted.map(c => [
      c.name,
      ...(showFirstInvoice ? [c.firstInvoice ?? ''] : []),
      c.revenue,
      (c.revenue / nMonths).toFixed(0),
      c.lastInvoice ?? '',
    ]);
    const csv = [cols.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'clients.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden">
      {/* Table header / controls */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200">
        <div className="flex-1 flex items-center gap-2">
          <h3 className="text-sm font-semibold text-slate-900">Clients</h3>
          <span className="text-[11px] font-semibold bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full tabular-nums">
            {sorted.length}
            {filtered.length !== clients.length && ` / ${clients.length}`}
          </span>
        </div>

        {/* Search */}
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-600" />
          <input
            type="text"
            placeholder="Search clients..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-7 pr-3 py-1.5 text-xs rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-48 text-slate-900 placeholder-slate-400"
          />
        </div>

        <button
          onClick={handleExport}
          title="Export to CSV"
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50 transition-colors"
        >
          <Download size={13} />
          Export
        </button>
      </div>

      {/* Table */}
      <div className="overflow-auto max-h-[480px]">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10">
            <tr className="bg-slate-100 border-b-2 border-slate-200">
              <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 sticky left-0 bg-slate-50 min-w-[200px]">
                <span className="inline-flex items-center gap-0.5 cursor-pointer hover:text-slate-800" onClick={() => handleSort('name')}>
                  Client <SortIcon k="name" />
                </span>
              </th>
              <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-500 min-w-[120px]">
                Type
              </th>
              {showFirstInvoice && (
                <Th k="firstInvoice" label="First Invoice" align="center" className="min-w-[120px]" />
              )}
              <Th k="revenue" label="Period Revenue" align="right" className="min-w-[140px]" />
              <Th k="avgPerMonth" label="Avg / Month" align="right" className="min-w-[110px]" />
              <Th k="lastInvoice" label="Last Invoice" align="center" className="min-w-[120px]" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading
              ? [...Array(8)].map((_, i) => (
                <tr key={i} className="bg-white">
                  {[...Array(showFirstInvoice ? 6 : 5)].map((_, j) => (
                    <td key={j} className="px-4 py-3">
                      <div className="h-4 bg-slate-100 rounded animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))
              : sorted.length === 0
              ? (
                <tr>
                  <td colSpan={showFirstInvoice ? 6 : 5} className="px-4 py-12 text-center text-sm text-slate-600">
                    No clients match the current filters
                  </td>
                </tr>
              )
              : sorted.map((c, i) => (
                <tr
                  key={`${c.id}-${i}`}
                  onClick={() => onClientClick?.(c)}
                  className={`group cursor-pointer transition-colors hover:bg-blue-50/60 ${i % 2 === 1 ? 'bg-slate-50/70' : 'bg-white'}`}
                >
                  <td className={`px-4 py-3 sticky left-0 group-hover:bg-blue-50/60 transition-colors ${i % 2 === 1 ? 'bg-slate-50/70' : 'bg-white'}`}>
                    <div className="font-medium text-slate-900 text-sm leading-tight">{c.name}</div>
                    {c.region && <div className="text-[11px] text-slate-600 mt-0.5">{c.region}</div>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1 flex-wrap">
                      {c.isNew && (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 ring-1 ring-blue-200">
                          New
                        </span>
                      )}
                      {c.isRecurring && (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-teal-50 text-teal-700 ring-1 ring-teal-200">
                          Recurring
                        </span>
                      )}
                      {!c.isNew && !c.isRecurring && (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-500">
                          Existing
                        </span>
                      )}
                    </div>
                  </td>
                  {showFirstInvoice && (
                    <td className="px-4 py-3 text-center text-sm text-slate-600 tabular-nums">
                      {c.firstInvoice
                        ? new Date(c.firstInvoice).toLocaleDateString('en-US', { year: '2-digit', month: 'short', day: 'numeric' })
                        : <span className="text-slate-300">—</span>
                      }
                    </td>
                  )}
                  <td className="px-4 py-3 text-right font-semibold text-slate-900 tabular-nums text-sm">
                    {fmtUSD(c.revenue)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-slate-600 tabular-nums">
                    {fmtUSD(c.revenue / nMonths)}
                  </td>
                  <td className="px-4 py-3 text-center text-sm text-slate-600 tabular-nums">
                    {c.lastInvoice
                      ? new Date(c.lastInvoice).toLocaleDateString('en-US', { year: '2-digit', month: 'short', day: 'numeric' })
                      : <span className="text-slate-300">—</span>
                    }
                  </td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ClientTable;
